import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "src/redux/store";
import { Icon } from "@iconify/react";
import bellFill from "@iconify-icons/eva/bell-fill";
import bellOutline from "@iconify-icons/eva/bell-outline";
import alertTriangle from "@iconify-icons/eva/alert-triangle-outline";
import calendarIcon from "@iconify-icons/eva/calendar-outline";
import clockIcon from "@iconify-icons/eva/clock-outline";
import fileTextIcon from "@iconify-icons/eva/file-text-outline";
import editIcon from "@iconify-icons/eva/edit-2-outline";
import PopoverMenu from "src/components/PopoverMenu";
import {
  useBookingAlertsData,
  BookingAlertsPanel,
} from "./BookingAlerts";
import {
  useScheduleAlertsData,
  ScheduleAlertsPanel,
} from "./ScheduleAlertsBadge";
import { fetchAeatErrorsCount } from "src/redux/slices/aeatErrors";
import { pollEmployeeCorrectionsCount } from "src/redux/slices/employeeCorrections";
import { fetchBookingAlerts } from "src/redux/slices/bookings";
import { fetchScheduleAlerts } from "src/redux/slices/scheduleAlerts";
import {
  readAdminNotification,
  markOneAdminAsRead,
  showModalNotificationStartup,
} from "src/redux/slices/notifications";
import useSystemNotificationHub from "src/utils/hubs/useSystemNotificationHub";
import useCashDrawerHub from "src/utils/hubs/useCashDrawerHub";
import useDeviceEnforcementHub from "src/utils/hubs/useDeviceEnforcementHub";
import useRolePermissionsHub from "src/utils/hubs/useRolePermissionsHub";
import useAddOnGrantsHub from "src/utils/hubs/useAddOnGrantsHub";
import useOnboardingProgress from "src/hooks/useOnboardingProgress";
import NotificationPreview from "./Notifications/NotificationPreview";
import NotificationsPreview from "./Notifications/NotificationMultiplePreview";
import { PATH_APP } from "src/routes/paths";

// Hook compartido para la tab "Hacienda". Polling cada 60s del contador del
// backend (count rápido). El listado completo se carga en /user/aeat-audit.
function useAeatErrorsData(enabled) {
  const dispatch = useDispatch();
  const { badgeCount } = useSelector((s) => s.aeatErrors);
  useEffect(() => {
    if (!enabled) return undefined;
    // Un solo fetch al montar. SignalR (NotificationRestauranteHub) emite
    // el evento cuando cambia el contador. Polling eliminado (era legacy
    // de cuando SignalR no era fiable — el HAR mostraba 15 llamadas por
    // sesión de 4 min por este endpoint).
    dispatch(fetchAeatErrorsCount());
  }, [dispatch, enabled]);
  return { count: badgeCount, hasCritical: badgeCount > 0 };
}

// Fetch inicial de las correcciones pendientes de aprobación. SignalR
// notifica los cambios; polling eliminado (era 11 llamadas por sesión).
function useEmployeeCorrectionsData(enabled) {
  const dispatch = useDispatch();
  const { items, badgeCount } = useSelector((s) => s.employeeCorrections);
  useEffect(() => {
    if (!enabled) return undefined;
    dispatch(pollEmployeeCorrectionsCount());
  }, [dispatch, enabled]);
  return { count: badgeCount, items };
}

// Panel para la tab "Correcciones". Lista compacta (top 5) + CTA a la
// vista completa. Acciones rápidas (Aprobar/Rechazar) se hacen desde la
// vista — aquí solo damos preview para no duplicar lógica del dialog.
function EmployeeCorrectionsPanel({ onClose }) {
  const history = useHistory();
  const { items, count } = useEmployeeCorrectionsData(true);
  const top = (items || []).slice(0, 5);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Correcciones de fichaje
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {count === 0
              ? "Sin propuestas pendientes."
              : `${count} propuesta${count === 1 ? "" : "s"} de empleados`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2.5, pb: 2 }}>
        {top.length > 0 ? (
          <Stack spacing={1}>
            {top.map((it) => (
              <Box
                key={it.id}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: "background.neutral",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {it.employeeName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {it.isNewShiftRequest
                    ? "Fichaje nuevo"
                    : !it.previousEnd && it.newEnd
                      ? "Cerrar olvidado"
                      : "Editar horas"}
                </Typography>
              </Box>
            ))}
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                onClose?.();
                history.push(PATH_APP.user.employeeCorrections);
              }}
            >
              Revisar todas
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              color: "text.disabled",
              borderRadius: 1.5,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2">
              Sin solicitudes pendientes. Volverá a comprobarse en 90 s.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Panel mínimo para la tab "Hacienda" del AlertsCenter. No replica el detalle
// (eso vive en /user/aeat-audit), solo informa y manda al panel completo.
function AeatErrorsPanel({ onClose }) {
  const history = useHistory();
  const { count } = useAeatErrorsData(true);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Hacienda (AEAT)
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {count === 0
              ? "Todos los envíos a Hacienda al día."
              : `${count} envío${count === 1 ? "" : "s"} pendiente${count === 1 ? "" : "s"} de resolver`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2.5, pb: 2 }}>
        {count > 0 ? (
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "warning.lighter",
                color: "warning.darker",
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Icon icon={alertTriangle} width={20} height={20} />
              <Typography variant="body2">
                Hay errores en envíos a Hacienda. El sistema reintenta solo
                los de conectividad; los de validación o certificado
                necesitan tu acción.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={() => {
                onClose?.();
                history.push(`${PATH_APP.user.aeatAudit}?tab=errores`);
              }}
            >
              Revisar y resolver
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              color: "text.disabled",
              borderRadius: 1.5,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2">
              Sin envíos pendientes. Volverá a comprobarse en 60 s.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Panel para la tab "Avisos" (SystemNotifications de plataforma
// admin→licencia). Muestra la lista completa de avisos que llegan al
// restaurante desde el equipo TakeMyOrder (mantenimiento, nuevas
// features, cambios contractuales…). Cada tarjeta:
//   • Bloque destacado si sin leer (fondo tinted).
//   • Título + resumen + timestamp.
//   • Click → abre el modal individual (`NotificationPreview`) que
//     marca como leído al cerrar (reusa el mismo componente que ya
//     usaba la campana antigua para no diverger).
function SystemNotificationsPanel({ onClose }) {
  const dispatch = useDispatch();
  const { adminNotifications } = useSelector((s) => s.notification);
  const [selected, setSelected] = useState(null);
  const list = Array.isArray(adminNotifications) ? adminNotifications : [];
  // Ordenar: primero las sin leer (más urgentes), luego por fecha desc.
  const sorted = [...list].sort((a, b) => {
    if (a.isUnRead !== b.isUnRead) return a.isUnRead ? -1 : 1;
    const da = new Date(a.creationDate || 0).getTime();
    const db = new Date(b.creationDate || 0).getTime();
    return db - da;
  });
  const unread = list.filter((n) => n.isUnRead).length;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Avisos de TakeMyOrder
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {sorted.length === 0
              ? "Sin avisos por ahora."
              : unread === 0
                ? `${sorted.length} aviso${sorted.length === 1 ? "" : "s"} · todos leídos`
                : `${unread} sin leer de ${sorted.length}`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2.5, pb: 2, maxHeight: 380, overflowY: "auto" }}>
        {sorted.length > 0 ? (
          <Stack spacing={1}>
            {sorted.slice(0, 12).map((n) => (
              <Box
                key={n.id}
                onClick={() => setSelected(n)}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  bgcolor: n.isUnRead ? "primary.lighter" : "background.neutral",
                  border: "1px solid",
                  borderColor: n.isUnRead ? "primary.light" : "divider",
                  transition: "transform .1s, box-shadow .15s",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: n.isUnRead ? 800 : 600,
                        color: n.isUnRead ? "primary.darker" : "text.primary",
                      }}
                      noWrap
                    >
                      {n.title || "(Sin título)"}
                    </Typography>
                    {n.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                          color: "text.secondary",
                          lineHeight: 1.35,
                        }}
                      >
                        {n.description}
                      </Typography>
                    )}
                  </Box>
                  {n.isUnRead && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        mt: 0.5,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            ))}
            {sorted.length > 12 && (
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", textAlign: "center", mt: 0.5 }}
              >
                Mostrando los 12 más recientes de {sorted.length}
              </Typography>
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              color: "text.disabled",
              borderRadius: 1.5,
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2">
              Cuando el equipo TakeMyOrder envíe avisos aparecerán aquí.
            </Typography>
          </Box>
        )}
      </Box>

      {selected && (
        <NotificationPreview
          open={!!selected}
          onClose={() => setSelected(null)}
          notification={selected}
          markAsRead={() => {
            if (selected?.isUnRead) dispatch(markOneAdminAsRead(selected.id));
            setSelected(null);
          }}
        />
      )}
    </Box>
  );
}

// Contenido de cada Tab del AlertsCenter — icono encima, label debajo,
// badge superpuesto en la esquina sup-der del icono. Sustituye al label
// inline anterior (texto + chip al lado) que con 4 tabs quedaba apretado
// y rompía a 2 líneas en mobile.
function TabContent({ icon, label, badge, badgeColor }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        position: "relative",
        width: "100%",
      }}
    >
      <Box sx={{ position: "relative", lineHeight: 0 }}>
        <Icon icon={icon} width={20} height={20} />
        {badge > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: -6,
              right: -10,
              bgcolor: badgeColor,
              color: "common.white",
              borderRadius: 999,
              minWidth: 16,
              height: 16,
              px: 0.5,
              fontSize: 10,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              boxShadow: "0 0 0 2px var(--mui-palette-background-neutral, #f4f6f8)",
            }}
          >
            {badge > 99 ? "99+" : badge}
          </Box>
        )}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          lineHeight: 1.1,
          // Defensivo: si el label es más largo que el ancho de la tab
          // (típico con 5 tabs en popover estrecho) permitimos wrap a
          // 2 líneas en vez de cortar con ellipsis invisible.
          textAlign: "center",
          whiteSpace: "normal",
          wordBreak: "break-word",
          width: "100%",
          px: 0.25,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

// Centro de alertas unificado del TopBar TPV. Fusiona en una sola campana
// las antiguas tres entradas (Reservas + Fichajes). El badge muestra la
// suma; el popover separa por tabs. Color de la campana refleja la alerta
// más urgente: error si hay críticos de fichaje (AUSENTE/SIN_CHECKOUT),
// warning si hay pendientes de reserva, gris si no hay nada accionable.
//
// Los hooks (useBookingAlertsData / useScheduleAlertsData) y los paneles
// vienen de los componentes originales — el polling y la lógica viven
// allí, este wrapper solo agrupa la presentación.
export default function AlertsCenter({
  showBookings = true,
  showSchedule = true,
  showAeat = true,
  showCorrections = true,
  showSystem = true,
}) {
  const dispatch = useDispatch();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Las tres fuentes — siempre las leemos (los hooks se encargan del polling
  // bajo el capó), aunque alguna tab esté oculta por permisos del usuario.
  const bookings = useBookingAlertsData();
  const schedule = useScheduleAlertsData();
  const aeat = useAeatErrorsData(showAeat);
  const corrections = useEmployeeCorrectionsData(showCorrections);

  // Avisos de plataforma (SystemNotification admin→licencia):
  //   • Hook SignalR → real-time cuando se crea/publica una nueva.
  //   • Fetch inicial al montar (antes vivía en el bell antiguo que
  //     dejamos de montar cuando unificamos el TopBar en AlertsCenter,
  //     y sin este fetch los avisos existentes no aparecían).
  //   • Modal bloqueante (`NotificationsPreview`) para las mandatorias
  //     sin leer — el hook dispara `showModalNotificationStartup(true)`.
  useSystemNotificationHub();
  // Hub SignalR del cajón monedero automático — se mantiene activo
  // mientras el TPV está abierto, así el PaymentDialog puede cerrarse
  // y aún así recibir el resultado del cajón cuando el hardware termine.
  useCashDrawerHub();
  // F-device-enforcement · 2026-08-24 — escucha al UserHub el evento
  // `device-enforcement/{id}` que el admin dispara al asignar empleado
  // forzado o activar "requerir selección" a un TPV. Si el evento
  // coincide con el `sessionIdentifier` local, guardamos los flags y
  // recargamos para aplicar los nuevos permisos.
  useDeviceEnforcementHub();
  // 2026-08-24 · Escucha cambios de permisos de rol. Si el rol del
  // empleado activo cambia, snackbar + auto-logout + kiosko para
  // re-fichar con el JWT nuevo (que trae actions actualizadas).
  useRolePermissionsHub();
  // 2026-08-27 · Escucha grants admin de add-ons. Cuando el admin
  // regala/retira un extra al restaurante, force-refetch de
  // activeAddOns para que nav y AddOnGate se actualicen sin F5.
  useAddOnGrantsHub();
  const { user, claims } = useSelector((s) => s.authJwt);
  const { adminNotifications, showModal } = useSelector((s) => s.notification);
  // Gate para no apilar modales encima del wizard onboarding o del
  // legal — el owner acaba de entrar y ya tiene 1-2 modales activos.
  // Los admin notifications mandatorios se apilan encima del wizard y
  // rompen la experiencia. Esperamos a que ambos flujos previos estén
  // resueltos antes de levantar el modal de notificaciones.
  const { wizardCompleted, blockedByLegal } = useOnboardingProgress();
  const systemUnread = Array.isArray(adminNotifications)
    ? adminNotifications.filter((n) => n.isUnRead).length
    : 0;
  const systemHydratedRef = useRef(false);
  const modalAutoOpenedRef = useRef(false);
  const isEmployeeWebSession = !!claims?.userEmployeeId;
  useEffect(() => {
    if (!showSystem) return;
    if (systemHydratedRef.current) return;
    systemHydratedRef.current = true;
    dispatch(readAdminNotification());
  }, [dispatch, showSystem]);

  // Auto-abrir el modal bloqueante al cargar la app si el owner tiene
  // avisos MANDATORIOS sin leer. Antes solo se abría vía SignalR (al
  // crearse una nueva), así que un aviso obligatorio ya existente al
  // login no dispara nada — el user podía operar sin verlo. Este
  // effect corrige el gap: tras el primer fetch, si hay 1+ mandatorio
  // pendiente lo levantamos. Solo dispara UNA vez por sesión de app
  // (ref-guard) para no reabrirlo si el user lo cierra manualmente.
  useEffect(() => {
    if (!showSystem || isEmployeeWebSession) return;
    if (!user?.id) return;
    if (modalAutoOpenedRef.current) return;
    if (!Array.isArray(adminNotifications) || adminNotifications.length === 0) return;
    // Gate: no apilar el modal bloqueante encima del legal o del
    // wizard onboarding. Esperamos a que ambos estén completados —
    // cuando el user cierre el wizard, este effect volverá a
    // evaluarse (wizardCompleted cambia) y disparará el modal.
    if (blockedByLegal) return;
    if (!wizardCompleted) return;
    const hasMandatoryUnread = adminNotifications.some(
      (n) => n.isMandatory && n.isUnRead,
    );
    if (!hasMandatoryUnread) return;
    modalAutoOpenedRef.current = true;
    dispatch(showModalNotificationStartup(true));
  }, [
    dispatch,
    showSystem,
    isEmployeeWebSession,
    user?.id,
    adminNotifications,
    blockedByLegal,
    wizardCompleted,
  ]);

  // Refresh proactivo de las 4 fuentes cuando el usuario abre la
  // campana. Salta `staleGuard` con `force: true` porque el motivo por
  // el que abre la campana es precisamente comprobar si hay novedades
  // — no queremos servirle un cache de hace 5 min.
  const refreshAll = useCallback(() => {
    if (showBookings) dispatch(fetchBookingAlerts({ force: true }));
    if (showSchedule) dispatch(fetchScheduleAlerts({ force: true }));
    if (showAeat) dispatch(fetchAeatErrorsCount({ force: true }));
    if (showCorrections) dispatch(pollEmployeeCorrectionsCount({ force: true }));
    if (showSystem) dispatch(readAdminNotification());
  }, [dispatch, showBookings, showSchedule, showAeat, showCorrections, showSystem]);

  // Polling silencioso cada 90s como fallback: SignalR cubre bookings
  // (HubBooking) pero schedule/hacienda/correcciones no tienen push
  // — sin este tick el badge se quedaba pillado hasta refrescar la
  // pagina. 90s es lo bastante reactivo para la operativa sin cargar
  // el backend (el fetch es 1 GET liviano de contador/lista corta).
  useEffect(() => {
    const id = setInterval(refreshAll, 90_000);
    return () => clearInterval(id);
  }, [refreshAll]);

  const bookingBadge = showBookings ? bookings.badge : 0;
  const scheduleBadge = showSchedule ? schedule.count : 0;
  const aeatBadge = showAeat ? aeat.count : 0;
  const correctionsBadge = showCorrections ? corrections.count : 0;
  const systemBadge = showSystem ? systemUnread : 0;
  const totalBadge = bookingBadge + scheduleBadge + aeatBadge + correctionsBadge + systemBadge;

  // Hacienda crítico cuenta como rojo igual que un fichaje crítico —
  // un envío fallido a AEAT es prioridad alta para el restaurante.
  const hasCritical = (showSchedule && schedule.hasCritical) || (showAeat && aeat.hasCritical);
  const hasPending = showBookings && bookings.hasPending;

  // Color del icono — error > warning > default. Coincide con el criterio
  // que cada campana usaba por su cuenta.
  const color = hasCritical
    ? "error.main"
    : hasPending || totalBadge > 0
      ? "warning.main"
      : "text.secondary";

  // Tab inicial: la que tenga más casos accionables. Si todo viene vacío,
  // abre en Reservas (más frecuente). Hacienda gana si hay críticos.
  const initialTab = aeatBadge > 0
    ? "aeat"
    : systemBadge > 0
      ? "system"
      : scheduleBadge > 0 && scheduleBadge >= bookingBadge
        ? "schedule"
        : "bookings";
  const [tab, setTab] = useState(initialTab);

  // Cuenta de tabs visibles para decidir si mostramos o no la barra de tabs.
  const visibleTabs = [
    showBookings && "bookings",
    showSchedule && "schedule",
    showAeat && "aeat",
    showCorrections && "corrections",
    showSystem && "system",
  ].filter(Boolean);
  const onlyOne = visibleTabs.length <= 1;
  const effectiveTab = onlyOne
    ? visibleTabs[0] || "bookings"
    : tab;

  return (
    <>
      <Tooltip
        title={
          totalBadge === 0
            ? "Centro de alertas"
            : `${totalBadge} alerta${totalBadge === 1 ? "" : "s"}`
        }
        arrow
      >
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={() => {
            // Refetch al abrir para que las tabs muestren datos frescos
            // en el momento en que las miras (SignalR solo cubre bookings).
            refreshAll();
            setOpen(true);
          }}
          // mr:0.5 compensa el ~6-8px que el badge sobresale por la
          // derecha con el nuevo translate(35%, -35%) — así el spacing
          // percibido con el botón vecino ("Fichar") queda igual que
          // antes sin robar visibilidad al icono.
          sx={{ color, mr: 0.5 }}
        >
          <Badge
            badgeContent={totalBadge}
            color={hasCritical ? "error" : "warning"}
            max={99}
            overlap="circular"
            // Badge asomando por la esquina superior derecha (NO
            // tapando la campana). El intento previo de meterlo
            // "dentro" para no separar del botón vecino ("Fichar")
            // tapaba completamente el icono cuando había 1+ alertas.
            // Ahora: badge pequeño en la esquina como notificación
            // móvil + margen derecho del IconButton para compensar
            // los ~6px que sobresale y mantener spacing correcto con
            // el botón vecino.
            sx={{
              "& .MuiBadge-badge": {
                transform: "scale(1) translate(35%, -35%)",
                minWidth: 18,
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                padding: "0 5px",
                border: "2px solid",
                borderColor: "background.paper",
              },
            }}
          >
            <Icon
              icon={totalBadge > 0 ? bellFill : bellOutline}
              width={26}
              height={26}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <PopoverMenu
        width={isMobile ? 360 : 560}
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
      >
        {!onlyOne && (
          <Tabs
            value={effectiveTab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.neutral",
              minHeight: 72,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                minHeight: 72,
                py: 1,
                px: 0.25,
                minWidth: 0,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 11,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 700,
                },
              },
            }}
          >
            {showBookings && (
              <Tab
                value="bookings"
                label={
                  <TabContent
                    icon={calendarIcon}
                    label="Reservas"
                    badge={bookingBadge}
                    badgeColor={hasPending ? "warning.main" : "info.main"}
                  />
                }
              />
            )}
            {showSchedule && (
              <Tab
                value="schedule"
                label={
                  <TabContent
                    icon={clockIcon}
                    label="Fichajes"
                    badge={scheduleBadge}
                    badgeColor={schedule.hasCritical ? "error.main" : "warning.main"}
                  />
                }
              />
            )}
            {showAeat && (
              <Tab
                value="aeat"
                label={
                  <TabContent
                    icon={fileTextIcon}
                    label="Hacienda"
                    badge={aeatBadge}
                    badgeColor="error.main"
                  />
                }
              />
            )}
            {showCorrections && (
              <Tab
                value="corrections"
                label={
                  <TabContent
                    icon={editIcon}
                    label="Cambios"
                    badge={correctionsBadge}
                    badgeColor="warning.main"
                  />
                }
              />
            )}
            {showSystem && (
              <Tab
                value="system"
                label={
                  <TabContent
                    icon={bellFill}
                    label="Avisos"
                    badge={systemBadge}
                    badgeColor="info.main"
                  />
                }
              />
            )}
          </Tabs>
        )}

        {effectiveTab === "bookings" && showBookings && (
          <BookingAlertsPanel onClose={() => setOpen(false)} />
        )}
        {effectiveTab === "schedule" && showSchedule && <ScheduleAlertsPanel />}
        {effectiveTab === "aeat" && showAeat && (
          <AeatErrorsPanel onClose={() => setOpen(false)} />
        )}
        {effectiveTab === "corrections" && showCorrections && (
          <EmployeeCorrectionsPanel onClose={() => setOpen(false)} />
        )}
        {effectiveTab === "system" && showSystem && (
          <SystemNotificationsPanel onClose={() => setOpen(false)} />
        )}

        {!showBookings && !showSchedule && !showAeat && !showCorrections && !showSystem && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No hay alertas disponibles para tu rol.
            </Typography>
          </Box>
        )}
      </PopoverMenu>

      {/* Modal bloqueante para SystemNotifications mandatorias sin leer.
          El hook `useSystemNotificationHub` dispara `showModalNotificationStartup`
          al recibir la creación/publicación en tiempo real. Vive fuera del
          PopoverMenu porque debe aparecer aunque la campana esté cerrada. */}
      {showSystem && showModal && (
        <NotificationsPreview
          open={showModal}
          onClose={() => dispatch(showModalNotificationStartup(false))}
          notifications={adminNotifications}
          markAsRead={(id) => dispatch(markOneAdminAsRead(id))}
        />
      )}
    </>
  );
}
